import type { CrudFilters } from '@refinedev/core';

/**
 * Reading back the filters refine restored from the URL (`syncWithLocation`).
 *
 * List toolbars keep their controls in local state, so every one of them has to
 * seed that state from the restored filters — otherwise the toolbar's own
 * debounced `setFilters(..., 'replace')` fires on mount with empty values and
 * erases what the reload just recovered.
 *
 * Everything arrives as a STRING: refine serialises filters with `qs` and parses
 * them back the same way, so `value: false` returns as `'false'`. That is why
 * `asBoolean` exists and why the data provider must use it too.
 */

type LooseFilter = { field?: string; operator?: string; value?: unknown };

// `CrudFilters` is a union (logical filters have `field`, conditional ones
// `key`); the toolbars only ever write logical filters, so narrow once here.
function findFilter(filters: CrudFilters | undefined, field: string): LooseFilter | undefined {
  return ((filters ?? []) as LooseFilter[]).find((filter) => filter.field === field);
}

/** The restored value for `field` as text, or `''` when the filter is absent. */
export function filterValue(filters: CrudFilters | undefined, field: string): string {
  const value = findFilter(filters, field)?.value;
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value : String(value);
}

/** The restored operator for `field` — how `null`/`nnull` toggles are read back. */
export function filterOperator(filters: CrudFilters | undefined, field: string): string | undefined {
  return findFilter(filters, field)?.operator;
}

/** Booleans survive the URL as `'true'` / `'false'`; anything else is "absent". */
export function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/** The restored boolean for `field`, or `undefined` when the filter is absent. */
export function filterFlag(filters: CrudFilters | undefined, field: string): boolean | undefined {
  return asBoolean(findFilter(filters, field)?.value);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Id filters must not be sent half-typed. The hub's query schemas declare
 * `format: 'uuid'`, so a partial value is a 400 — and refine keeps the previous
 * page's rows when a query fails, which presents the OLD result as the filtered
 * one. Treat an incomplete id as "not filtering yet" instead.
 */
export function isUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}
